import {
  Box,
  Button,
  FormControl,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  Etapa,
  useGetEdicaoAtualQuery,
  useGetListaEdicoesQuery,
} from "shared/features/api/edicao/edicaoSlice";
import { IInscricao } from "shared/features/api/inscricao/types";
import { useLazyGetInscricaoFiltroQuery } from "shared/features/api/inscricao/inscricaoSlice";
import { useAvaliacaoPorEtapa } from "shared/hooks/useAvaliacaoEtapa";
import { FormFilter } from "components/FormFilter";
import { useGetEtapaAtualQuery } from "shared/features/api/etapa/etapaSlice";

export const Avaliation: React.FC = () => {
  const { data: edicaoAtual, isLoading: carregandoEdicaoAtual } =
    useGetEdicaoAtualQuery();
  const { data: edicoes, isLoading: carregandoEdicoes } =
    useGetListaEdicoesQuery();
  const [buscarInscricoes] = useLazyGetInscricaoFiltroQuery();

  const { data: etapaAtual, isSuccess } = useGetEtapaAtualQuery(
    edicaoAtual?.idEdicao,
    {
      skip: !edicaoAtual,
    }
  );

  const [etapasEdicao, setEtapasEdicao] = useState<Etapa[]>([]);
  const [etapaSelecionada, setEtapaSelecionada] = useState<number>(0);
  const [etapa, setEtapa] = useState<Etapa>({} as Etapa);
  const [nomeOuEmail, setNomeOuEmail] = useState<string>("");
  const [debouncedNomeOuEmail, setDebouncedNomeOuEmail] = useState("");
  const [edicaoNome, setEdicaoNome] = useState<string>("");
  const [trilha, setTrilha] = useState<string>("");
  const [statusEtapa, setStatusEtapa] = useState<string>("");
  const [pagina, setPagina] = useState<number>(0);
  const [tamanho, setTamanho] = useState<number>(10);
  const [inscricoes, setInscricoes] = useState<IInscricao | undefined>(
    undefined
  );

  const { ComponenteEtapa } = useAvaliacaoPorEtapa(etapa.nome || "");

  useEffect(() => {
    if (edicaoAtual) {
      const sortedEtapas = [...edicaoAtual.etapas].sort(
        (a, b) => a.ordem - b.ordem
      );
      setEtapasEdicao(sortedEtapas);
      setEtapaSelecionada(0);
      setEdicaoNome(edicaoAtual.nome);
    }
  }, [edicaoAtual]);

  useEffect(() => {
    if (etapasEdicao.length > 0 && etapaSelecionada !== undefined) {
      const novaEtapa = etapasEdicao[etapaSelecionada];
      if (novaEtapa?.nome) setEtapa(novaEtapa);
    }
  }, [etapaSelecionada, etapasEdicao]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedNomeOuEmail(nomeOuEmail);
    }, 500);

    return () => clearTimeout(timeout);
  }, [nomeOuEmail]);

  useEffect(() => {
    if (edicaoNome && etapa) {
      buscarInscricoesEtapas();
    }
  }, [edicaoNome, etapa, statusEtapa, debouncedNomeOuEmail, trilha]);

  useEffect(() => {
    if (isSuccess && etapaAtual && etapaAtual.idEtapa !== null) {
      setEtapaSelecionada(etapaAtual.idEtapa - 1);
    }
  }, [etapaAtual, isSuccess]);

  const buscarInscricoesEtapas = async () => {
    let etapaLink = `&etapa=${etapa.nome}`;
    let edicaoLink = `&edicao=${edicaoNome}`;
    let paginaLink = `&pagina=${pagina}`;
    let tamanhoLink = `&tamanho=${tamanho}`;
    let nomeOuEmailLink = nomeOuEmail ? `&nomeOuEmail=${nomeOuEmail}` : "";
    let statusEtapaLink = statusEtapa ? `&statusEtapa=${statusEtapa}` : "";
    let trilhaLink = trilha ? `&trilha=${trilha}` : "";

    let queryParams =
      paginaLink +
      tamanhoLink +
      edicaoLink +
      etapaLink +
      statusEtapaLink +
      nomeOuEmailLink +
      trilhaLink;

    buscarInscricoes({ url: queryParams })
      .unwrap()
      .then((data) => {
        setInscricoes(data);
      });
  };

  return (
    <Grid
      container
      spacing={2}
      sx={{
        display: "flex",
        justifyContent: "center",
        px: { xs: 1, sm: 2, md: 4 },
        py: { xs: 1, sm: 2 },
      }}
    >
      {carregandoEdicaoAtual || carregandoEdicoes ? (
        <LinearProgress />
      ) : (
        <>
          <Grid
            item
            xs={12}
            sx={{
              width: "100%",
              px: { xs: 1, sm: 2 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mx: "auto",
                width: "100%",
              }}
            >
              <Typography
                fontSize={{ xs: 16, sm: 18 }}
                fontWeight={600}
                sx={{ whiteSpace: "nowrap" }}
              >
                {edicaoNome}
              </Typography>
              <Box
                component={"form"}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 120,
                    maxWidth: 250,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  <Select
                    id="avaliacao-filter-edicao"
                    value={edicaoNome}
                    onChange={(e) => setEdicaoNome(e.target.value)}
                    fullWidth
                  >
                    {edicoes?.map((edicao) => {
                      return (
                        <MenuItem value={edicao.nome}>{edicao.nome}</MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                /* padding: { xs: 1, sm: 2 }, */
              }}
            >
              {etapasEdicao.length > 0 && (
                <Stepper
                  activeStep={etapaSelecionada}
                  sx={{
                    display: "flex",
                    backgroundColor: "transparent",
                    width: "80%",
                    mt: { xs: 1, lg: 2 },
                    overflowX: { xs: "auto", sm: "auto", md: "inicial" },

                    pb: { xs: 0, sm: 2 },
                  }}
                >
                  {etapasEdicao.map((etapa, index) => (
                    <Step
                      key={etapa.idEtapa || etapa.nome}
                      sx={{
                        backgroundColor: "transparent",
                        flex: 1,
                      }}
                      onClick={() => setEtapaSelecionada(index)}
                    >
                      <StepLabel
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          width: "100%",
                          fontSize: { xs: 10, sm: 12 },
                          "&:hover": {
                            cursor: "pointer",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: {
                              xs: "column",
                              lg: "row",
                            },
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                            width: "100%",
                          }}
                        >
                          <Typography
                            variant="body2"
                            textAlign="center"
                            fontSize={{ xs: 10, sm: 12 }}
                          >
                            {etapa.nome}
                          </Typography>
                        </Box>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} lg={12} md={12} xl={10}>
            {inscricoes && etapa && ComponenteEtapa && (
              <ComponenteEtapa
                etapa={etapa}
                inscricoes={inscricoes}
                setSearch={(valor: string) => setNomeOuEmail(valor)}
                page={pagina}
                setPage={(pagina: number) => setPagina(pagina)}
                setStatusEtapa={setStatusEtapa}
                statusEtapa={statusEtapa}
                trilha={trilha}
                setTrilha={setTrilha}
              />
            )}
          </Grid>
        </>
      )}
    </Grid>
  );
};
